import type { GeometryElement as JXGElement } from 'jsxgraph';
import type { BoundLabel } from '@examdojo/core/widget';
import type { PortMap, PointPort, EdgePort } from '../../../port';
import type { GeometryPadElementContext, CreatedGeometryElement } from '../../element.model';
import { FEATURE_LINE_DEFAULTS } from '../feature.defaults';
import type { AltitudeElement } from './altitude.model';

/**
 * Opposite-edge lookup for triangles.
 * Vertex A → edge BC, vertex B → edge CA, vertex C → edge AB.
 */
const TRIANGLE_OPPOSITE_EDGE: Record<string, string> = {
  A: 'edge.BC',
  B: 'edge.CA',
  C: 'edge.AB',
};

/**
 * Opposite-edge lookup for quadrilaterals (drop perpendicular to opposite parallel side).
 * A, B are on the bottom -> drop to top; C, D are on the top -> drop to bottom.
 */
const QUAD_OPPOSITE_EDGE: Record<string, string> = {
  A: 'edge.top',
  B: 'edge.top',
  C: 'edge.bottom',
  D: 'edge.bottom',
};

/**
 * Create an altitude line from a vertex perpendicular to the opposite edge.
 *
 * Reads the vertex and opposite edge from the parent shape's port registry,
 * computes the perpendicular foot, and draws a dashed line.
 */
export function createAltitude(
  element: AltitudeElement & { id: string },
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const { elementFactory, portRegistry, displayConfig } = context;
  const jsxObjects: JXGElement[] = [];
  const labels: BoundLabel[] = [];

  const parentId = element.params.of;
  const fromVertex = element.params.from;

  // Per-element display overrides
  const elementDisplay = displayConfig.elements?.[element.id];

  // Resolve the vertex position
  const getVertex = portRegistry.getGetter(`${parentId}.point.${fromVertex}`);

  // Determine the opposite edge port path
  const oppositeEdgeName = TRIANGLE_OPPOSITE_EDGE[fromVertex] ?? QUAD_OPPOSITE_EDGE[fromVertex];
  if (!oppositeEdgeName) {
    throw new Error(`Altitude: unknown vertex "${fromVertex}" — cannot determine opposite edge.`);
  }
  const getEdge = portRegistry.getGetter(`${parentId}.${oppositeEdgeName}`);

  // Compute foot and draw the line at creation time
  const vertex = getVertex() as PointPort;
  const edge = getEdge() as EdgePort;
  const foot = perpendicularFoot(vertex, edge.start, edge.end);

  const lineStyle = {
    color: elementDisplay?.line?.color ?? FEATURE_LINE_DEFAULTS.color,
    strokeWidth: elementDisplay?.line?.strokeWidth ?? FEATURE_LINE_DEFAULTS.strokeWidth,
    dash: elementDisplay?.line?.dash ?? FEATURE_LINE_DEFAULTS.dash,
    visible: elementDisplay?.visible ?? true,
    label: elementDisplay?.line?.label ?? (false as const),
    ...(elementDisplay?.line ?? {}),
    vertex: { fixed: true, ...(elementDisplay?.point ?? {}) },
  };

  const line = elementFactory.line({
    a: vertex,
    b: foot,
    name: `${element.id}-alt`,
    style: lineStyle,
  });

  jsxObjects.push(line.line, line.a, line.b);

  // --- Build ports ---
  const ports: PortMap = {
    length: () => {
      const v = getVertex() as PointPort;
      const e = getEdge() as EdgePort;
      const f = perpendicularFoot(v, e.start, e.end);
      return dist(v, f);
    },
    foot: () => {
      const v = getVertex() as PointPort;
      const e = getEdge() as EdgePort;
      return perpendicularFoot(v, e.start, e.end);
    },
  };

  return {
    jsxObjects,
    labels,
    ports,
    destroy: () => {
      const board = elementFactory.getContext().board;
      for (const label of labels) {
        board.removeObject(label);
      }
      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Math helpers
// ---------------------------------------------------------------------------

/**
 * Project point P onto the line defined by segment (start → end).
 *
 * foot = start + ((P - start) · (end - start)) / |end - start|² * (end - start)
 */
function perpendicularFoot(p: PointPort, start: PointPort, end: PointPort): PointPort {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const lenSq = dx * dx + dy * dy;

  // Degenerate edge: start == end → foot is the start point
  if (lenSq < 1e-12) {
    return [start[0], start[1]];
  }

  const t = ((p[0] - start[0]) * dx + (p[1] - start[1]) * dy) / lenSq;
  return [start[0] + t * dx, start[1] + t * dy];
}

function dist(a: PointPort, b: PointPort): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  return Math.sqrt(dx * dx + dy * dy);
}
